import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import adminAxios from "../../../api/adminAxios";
import * as XLSX from "xlsx";

const formatDate = (d) => {
  if (!d) return "N/A";
  return new Date(d).toLocaleString("en-IN");
};

const DeliveredOrders = () => {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchDeliveredOrders = async () => {
      try {
        const res = await adminAxios.get("/api/admin/orders/delivered");
        setOrders(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error("Error fetching delivered orders:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDeliveredOrders();
  }, []);

  // =========================
  // ✅ EXCEL DOWNLOAD FUNCTION
  // =========================
  const downloadExcel = () => {
    if (!orders.length) return;

    const formattedData = orders.map((order) => ({
      "Order ID": order.orderId || "N/A",
      "Status":
        order.latestStatus?.status || order.status || "Delivered",
      "Date": formatDate(
        order.latestStatus?.timestamp ||
          order.updatedAt ||
          order.createdAt
      ),

      "User Name": order.userName || "N/A",
      "User Email": order.userEmail || "N/A",

      "Seller Name": order.sellerName || "N/A",
      "Seller Email": order.sellerEmail || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Delivered Orders"
    );

    // ✅ Direct download
    XLSX.writeFile(
      workbook,
      `Delivered_Orders_${Date.now()}.xlsx`
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <p className="text-lg font-semibold text-gray-600">
          Loading Delivered Orders...
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">

        {/* HEADER */}
        <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-gray-900">
            Delivered Orders
          </h1>

          <div className="flex gap-2">
            {/* ✅ DOWNLOAD BUTTON */}
            <button
              onClick={downloadExcel}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              ⬇ Download Excel
            </button>

            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Back
            </button>
          </div>
        </div>

        {/* TABLE */}
        <div className="bg-white border rounded-lg shadow-sm overflow-x-auto">
          {orders.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-5 py-3 text-left">Order ID</th>
                  <th className="px-5 py-3 text-left">User</th>
                  <th className="px-5 py-3 text-left">Seller</th>
                  <th className="px-5 py-3 text-left">Date</th>
                  <th className="px-5 py-3 text-left">Status</th>
                  <th className="px-5 py-3 text-left">Action</th>
                </tr>
              </thead>

              <tbody>
                {orders.map((order, index) => {
                  const dateValue =
                    order.latestStatus?.timestamp ||
                    order.updatedAt ||
                    order.createdAt;

                  return (
                    <tr
                      key={order._id || index}
                      className="border-t hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelected(order)}
                    >
                      <td className="px-5 py-4 font-semibold">
                        {order.orderId || "N/A"}
                      </td>

                      <td className="px-5 py-4">
                        <div className="font-medium">
                          {order.userName || "N/A"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {order.userEmail}
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <div className="font-medium">
                          {order.sellerName || "N/A"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {order.sellerEmail}
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        {formatDate(dateValue)}
                      </td>

                      <td className="px-5 py-4 text-green-700 font-semibold">
                        {order.latestStatus?.status ||
                          order.status ||
                          "Delivered"}
                      </td>

                      <td className="px-5 py-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/orders/${order.orderId}`);
                          }}
                          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="p-6 text-center text-gray-500">
              No delivered orders found.
            </div>
          )}
        </div>

        {/* MODAL */}
        {selected && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-lg border w-full max-w-lg">
              <div className="flex justify-between items-center border-b p-4">
                <h3 className="font-bold">Order Quick Details</h3>

                <button
                  onClick={() => setSelected(null)}
                  className="px-3 py-1 bg-gray-200 rounded"
                >
                  Close
                </button>
              </div>

              <div className="p-4 text-sm space-y-2">
                <div>Order ID: {selected.orderId}</div>
                <div>
                  Status:{" "}
                  {selected.latestStatus?.status ||
                    selected.status ||
                    "Delivered"}
                </div>
                <div>
                  Date:{" "}
                  {formatDate(
                    selected.latestStatus?.timestamp ||
                      selected.updatedAt ||
                      selected.createdAt
                  )}
                </div>
                <div>User: {selected.userName}</div>
                <div>Seller: {selected.sellerName}</div>
              </div>

              <div className="flex justify-end gap-2 border-t p-4">
                <button
                  onClick={() => setSelected(null)}
                  className="px-4 py-2 bg-gray-200 rounded"
                >
                  Close
                </button>

                <button
                  onClick={() =>
                    navigate(`/orders/${selected.orderId}`)
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded"
                >
                  Full View
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveredOrders;